// Режим «На утро» (T-055, решение владельца 2026-10-08): тумблер в шапке киоска.
//
// Включён — основная кнопка печатает этикетки временем изготовления ближайшего утра,
// выключен — как обычно. Тумблер есть, только если админ сети включил печать на утро
// для точки (`default_location_preprint_time` в профиле).
//
// Режим, а не разовая опция: вечером печатают пачку на утро. Поэтому он обязан сам
// выключаться — иначе днём этикетки молча уходили бы «на завтра»:
//   • в момент того утра, на которое включён (дальше «на утро» значит уже следующее);
//   • при смене человека на устройстве и при перезапуске приложения — стор в памяти.
//
// Запоминается МОМЕНТ ВКЛЮЧЕНИЯ, а не само утро. Утро каждый раз считается из ТЕКУЩЕЙ
// настройки точки: админ сменил 10:00 на 09:00 при включённом тумблере — этикетка
// обязана уйти на 09:00 (пилот 2026-10-08: уходила на 10:00, запомненное при включении).

import { useEffect, useState } from 'react';
import { create } from 'zustand';

import { useSession } from '@/shared/session';

import { nextMorning } from './moment';

type MorningModeState = {
  /** Кто и когда включил (мс). `null` — выключен. */
  readonly armed: { readonly userId: string; readonly at: number } | null;
  readonly arm: (userId: string, at: Date) => void;
  readonly disarm: () => void;
};

export const useMorningModeStore = create<MorningModeState>()((set) => ({
  armed: null,
  arm: (userId, at) => set({ armed: { userId, at: at.getTime() } }),
  disarm: () => set({ armed: null }),
}));

export type MorningMode = {
  /** Админ включил печать на утро для точки — тумблер показывается. */
  readonly available: boolean;
  readonly on: boolean;
  /** Время утра «ЧЧ:ММ» из настройки точки. */
  readonly time: string | null;
  /** Утро, на которое уйдёт этикетка, пока режим включён; иначе `null`. */
  readonly morning: Date | null;
  readonly toggle: () => void;
};

/** «Сейчас» с шагом в полминуты — чтобы режим гас вовремя, а подпись не врала. */
function useTick(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

export function useMorningMode(): MorningMode {
  const profile = useSession().data ?? null;
  const time = profile?.default_location_preprint_time ?? null;
  const timeZone = profile?.default_location_timezone ?? null;
  const userId = profile?.id ?? null;
  const { armed, arm, disarm } = useMorningModeStore();

  const available = time !== null && timeZone !== null && userId !== null;
  const now = useTick(armed !== null);
  // Утро, на которое тумблер включён: первое наступление ТЕКУЩЕГО времени настройки
  // после момента включения.
  const until =
    armed !== null && time !== null && timeZone !== null
      ? nextMorning(time, timeZone, new Date(armed.at))
      : null;
  const on =
    available &&
    armed !== null &&
    armed.userId === userId &&
    until !== null &&
    now < until.getTime();

  // Погасить то, что больше не действует: утро наступило, человек сменился, админ
  // выключил функцию. В эффекте, а не в рендере: стор общий для шапки и футера.
  useEffect(() => {
    if (armed !== null && !on) disarm();
  }, [armed, on, disarm]);

  const morning = on ? until : null;

  function toggle(): void {
    if (!available || time === null || timeZone === null || userId === null) return;
    if (on) {
      disarm();
      return;
    }
    arm(userId, new Date());
  }

  return { available, on, time, morning, toggle };
}
